import type { SortState } from "@/features/identity-hub/lib/sort";

/** What a column contributes to the comparison; null means "nothing to compare". */
export type SortValue = number | string | null;

/**
 * Sorts rows in memory.
 *
 * These tables render a complete aggregate — every model or tenant that spent
 * anything in the window, already summed by the server — so there is no page to
 * re-fetch and no ranking the server could do that this cannot. Sorting stays
 * on the client, which also keeps the sort instant while a refetch is in flight.
 *
 * The sort is stable, so equal values keep the order the server ranked them in:
 * two tenants tied on calls stay ordered by spend.
 */
export function sortTableRows<T, K extends string>(
  rows: T[],
  sort: SortState<K>,
  valueOf: (row: T, key: K) => SortValue,
): T[] {
  const factor = sort.dir === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const left = valueOf(a, sort.key);
    const right = valueOf(b, sort.key);

    // A row with no value sinks to the bottom in both directions. A model that
    // never reported a latency is not the fastest one.
    if (left === null || right === null) {
      if (left === right) return 0;
      return left === null ? 1 : -1;
    }

    if (typeof left === "string" || typeof right === "string") {
      return factor * String(left).localeCompare(String(right), undefined, { sensitivity: "base" });
    }

    return factor * (left - right);
  });
}

/**
 * The same click contract as the IAM tables — re-clicking the active column
 * flips it — except that a fresh magnitude column opens *descending*.
 *
 * Every number in these tables is a cost, and the question behind the click is
 * always which row is the biggest. Opening ascending would answer with the
 * cheapest model on the platform and make a second click mandatory.
 */
export function toggleUsageSort<K extends string>(
  prev: SortState<K>,
  key: K,
  textKeys: ReadonlySet<K>,
): SortState<K> {
  if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
  return { key, dir: textKeys.has(key) ? "asc" : "desc" };
}
